import type { JSONContent } from "@tiptap/core";
import type { PostgrestError } from "@supabase/supabase-js";
import {
  mapContinuationSuggestionRows,
  mapStructuralAnalysisRows,
  mapContinuityIssueRows,
  mapContinuityIssueToView,
} from "@/features/ai/aiMappers";
import type {
  ContinuationSuggestionSessionView,
  StructuralAnalysisRunView,
  ContinuityIssueResolutionStatus,
  ContinuityIssueView,
} from "@/features/ai/ai.types";
import type { Database } from "@/lib/database.types";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  mapChapterRecordToView,
  mapMainStoryRecordToView,
  mapSideStoryRecordToView,
  mapStoryCapacityRow,
  normalizeStoryDocument,
  parseStoryLimitError,
} from "@/features/story/mappers/storyMappers";
import {
  createChapterSchema,
  createMainStorySchema,
  createSideStorySchema,
  updateChapterSchema,
  updateMainStorySchema,
  updateSideStorySchema,
} from "@/features/story/validation/storySchemas";
import { createStoryImageSignedUrl, deleteStoryImage } from "@/features/story/api/storyStorage";
import {
  chapterStatusLabels,
  cloneEmptyStoryDocument,
  type ChapterStatus,
  ChapterView,
  ChapterRecord,
  CreateChapterInput,
  CreateMainStoryInput,
  CreateSideStoryInput,
  MainStoryView,
  SideStoryView,
  StoryAttachmentView,
  StoryCapacitySummary,
  StoryRecord,
  StoryWorkspaceView,
  UpdateChapterInput,
  UpdateMainStoryInput,
  UpdateSideStoryInput,
} from "@/types/story";

type StoryRow = Database["public"]["Tables"]["stories"]["Row"];
type ChapterRow = Database["public"]["Tables"]["chapters"]["Row"];
type ContinuationSuggestionRow =
  Database["public"]["Tables"]["continuation_suggestions"]["Row"];
type ContinuityIssueRow = Database["public"]["Tables"]["continuity_issues"]["Row"];
type StructuralAnalysisRunRow =
  Database["public"]["Tables"]["ai_analysis_runs"]["Row"];

export type StoryActionErrorCode =
  | "generic"
  | "missing-config"
  | "access-denied"
  | "not-found"
  | "chapter-limit-reached"
  | "side-story-limit-reached"
  | "main-story-already-exists"
  | "mode-locked";

interface StoryActionErrorOptions {
  cause?: unknown;
  limitPrompt?: ReturnType<typeof parseStoryLimitError>;
}

export class StoryActionError extends Error {
  readonly cause?: unknown;
  readonly limitPrompt?: ReturnType<typeof parseStoryLimitError>;

  constructor(
    public readonly code: StoryActionErrorCode,
    message?: string,
    options?: StoryActionErrorOptions,
  ) {
    super(message ?? code);
    this.name = "StoryActionError";
    this.cause = options?.cause;
    this.limitPrompt = options?.limitPrompt;
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new StoryActionError(
      "missing-config",
      "Supabase environment variables are not configured.",
    );
  }
}

async function getCurrentUserId() {
  ensureConfigured();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new StoryActionError("access-denied", "No authenticated user available.");
  }

  return user.id;
}

function coerceImagePaths(value: Database["public"]["Tables"]["stories"]["Row"]["image_paths"]) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function hydrateStoryRecord(record: StoryRow): StoryRecord {
  return {
    ...record,
    body: normalizeStoryDocument(record.body),
    image_paths: coerceImagePaths(record.image_paths),
  };
}

function hydrateChapterRecord(record: ChapterRow): ChapterRecord {
  return {
    ...record,
    body: normalizeStoryDocument(record.body),
  };
}

async function getStoryImageUrl(path: string | null) {
  if (!path) {
    return null;
  }

  try {
    return await createStoryImageSignedUrl(path);
  } catch {
    return null;
  }
}

function isJsonContent(value: unknown): value is JSONContent {
  return Boolean(value) && typeof value === "object" && "type" in (value as Record<string, unknown>);
}

function createStoryImportDocument(text: string): JSONContent {
  const trimmed = text.trim();
  if (!trimmed) {
    return cloneEmptyStoryDocument();
  }

  const paragraphs = trimmed
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return {
    content: paragraphs.map((paragraph) => ({
      content: [{
        text: paragraph,
        type: "text",
      }],
      type: "paragraph",
    })),
    type: "doc",
  };
}

function collectStoryDocumentText(content: JSONContent | null | undefined): string {
  if (!content || typeof content !== "object") {
    return "";
  }

  const text: string[] = [];
  const stack: JSONContent[] = [content];

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) {
      continue;
    }

    if (typeof node.text === "string") {
      text.push(node.text);
    }

    if (Array.isArray(node.content)) {
      for (let index = node.content.length - 1; index >= 0; index -= 1) {
        stack.push(node.content[index] as JSONContent);
      }
    }
  }

  return text.join(" ").replace(/\s+/g, " ").trim();
}

export function buildImportedChapterView(input: {
  displayTitle: string;
  elementData: Record<string, unknown>;
  projectId: string;
  storyId: string;
}): ChapterView {
  const body = isJsonContent(input.elementData.body)
    ? input.elementData.body
    : createStoryImportDocument(String(input.elementData.plainText ?? ""));
  const title =
    typeof input.elementData.title === "string" && input.elementData.title.trim().length > 0
      ? input.elementData.title.trim()
      : input.displayTitle;
  const sortOrder =
    typeof input.elementData.sortOrder === "number" ? input.elementData.sortOrder : 0;
  const now = new Date().toISOString();

  return {
    body,
    createdAt: now,
    hasHeaderImage: false,
    headerGenerateActionLabel: "story.attachments.generateIllustration",
    headerImageUrl: null,
    headerTargetLabel: "Chapter image",
    id: "import-review-chapter",
    imagePath: null,
    imageUrl: null,
    isClosing: false,
    isOpening: false,
    sortOrder,
    status: "draft",
    statusLabel: chapterStatusLabels.draft,
    storyId: input.storyId,
    title,
    updatedAt: now,
  };
}

export function buildImportedChapterElementData(
  input: UpdateChapterInput,
  baseElementData: Record<string, unknown> = {},
) {
  return {
    ...baseElementData,
    body: input.body,
    plainText: collectStoryDocumentText(input.body),
    sortOrder:
      typeof baseElementData.sortOrder === "number" ? baseElementData.sortOrder : 0,
    status: input.status satisfies ChapterStatus,
    title: input.title,
  };
}

export async function resolveSideStoryView(
  record: StoryRecord,
  previousAttachments: StoryAttachmentView[] = [],
): Promise<SideStoryView> {
  const previousUrlByPath = new Map(
    previousAttachments
      .filter((attachment) => attachment.imageUrl)
      .map((attachment) => [attachment.path, attachment.imageUrl]),
  );

  const imageUrls = await Promise.all(
    record.image_paths.map(async (path) => previousUrlByPath.get(path) ?? getStoryImageUrl(path)),
  );

  return mapSideStoryRecordToView(record, imageUrls);
}

function sortStoryRows(records: StoryRow[]) {
  return [...records].sort((left, right) => {
    if (left.type !== right.type) {
      return left.type === "main" ? -1 : 1;
    }

    return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
  });
}

function mapStoryError(error: unknown): StoryActionError {
  if (error instanceof StoryActionError) {
    return error;
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String(error.message)
      : "Story action failed.";

  if (message === "MAIN_STORY_ALREADY_EXISTS") {
    return new StoryActionError("main-story-already-exists", message, { cause: error });
  }

  if (message === "STORY_MODE_LOCKED") {
    return new StoryActionError("mode-locked", message, { cause: error });
  }

  const limitPrompt = parseStoryLimitError(message);
  if (limitPrompt) {
    return new StoryActionError(
      limitPrompt.reason === "chapter_limit_reached"
        ? "chapter-limit-reached"
        : "side-story-limit-reached",
      message,
      {
        cause: error,
        limitPrompt,
      },
    );
  }

  const postgrestError = error as PostgrestError | undefined;
  if (postgrestError?.code === "PGRST116") {
    return new StoryActionError("not-found", "That story record could not be found.", {
      cause: error,
    });
  }

  if (postgrestError?.code === "42501" || message.toLowerCase().includes("row-level security")) {
    return new StoryActionError("access-denied", "You do not have access to that project.", {
      cause: error,
    });
  }

  return new StoryActionError("generic", message, { cause: error });
}

export function formatStoryErrorMessage(error: unknown) {
  const storyError = mapStoryError(error);

  switch (storyError.code) {
    case "missing-config":
      return "story.messages.missingConfig";
    case "access-denied":
      return "story.messages.accessDenied";
    case "not-found":
      return "story.messages.notFound";
    case "chapter-limit-reached":
    case "side-story-limit-reached":
      return "story.messages.limitReached";
    case "main-story-already-exists":
      return "story.messages.mainStoryExists";
    case "mode-locked":
      return "story.messages.modeLocked";
    default:
      return "story.messages.saveError";
  }
}

async function getOwnedStoryById(storyId: string) {
  ensureConfigured();

  const { data, error } = await supabase
    .from("stories")
    .select("*")
    .eq("id", storyId)
    .maybeSingle();

  if (error) {
    throw mapStoryError(error);
  }

  return data ? hydrateStoryRecord(data as StoryRow) : null;
}

async function getOwnedChapterById(chapterId: string) {
  ensureConfigured();

  const { data, error } = await supabase
    .from("chapters")
    .select("*")
    .eq("id", chapterId)
    .maybeSingle();

  if (error) {
    throw mapStoryError(error);
  }

  return data ? hydrateChapterRecord(data as ChapterRow) : null;
}

async function loadChapterViews(mainStoryId: string) {
  const { data, error } = await supabase
    .from("chapters")
    .select("*")
    .eq("story_id", mainStoryId)
    .order("sort_order", { ascending: true });

  if (error) {
    throw mapStoryError(error);
  }

  return Promise.all(
    ((data as ChapterRow[] | null) ?? []).map(async (row) => {
      const record = hydrateChapterRecord(row);
      const imageUrl = await getStoryImageUrl(record.image_path);
      return mapChapterRecordToView(record, imageUrl);
    }),
  );
}

export async function loadChapterContinuationSuggestions(
  chapterId: string,
): Promise<ContinuationSuggestionSessionView[]> {
  ensureConfigured();

  const { data, error } = await supabase
    .from("continuation_suggestions")
    .select("*")
    .eq("chapter_id", chapterId)
    .order("created_at", { ascending: false });

  if (error) {
    throw mapStoryError(error);
  }

  return mapContinuationSuggestionRows((data as ContinuationSuggestionRow[] | null) ?? []);
}

export async function loadChapterContinuityIssues(
  projectId: string,
  chapterId: string,
): Promise<ContinuityIssueView[]> {
  ensureConfigured();

  const { data, error } = await supabase
    .from("continuity_issues")
    .select("*")
    .eq("project_id", projectId)
    .eq("chapter_id", chapterId)
    .order("created_at", { ascending: false });

  if (error) {
    throw mapStoryError(error);
  }

  return mapContinuityIssueRows(projectId, (data as ContinuityIssueRow[] | null) ?? []);
}

export async function loadStructuralAnalysisRuns(
  projectId: string,
  storyId: string,
  chapters: ChapterView[],
): Promise<StructuralAnalysisRunView[]> {
  ensureConfigured();

  const { data, error } = await supabase
    .from("ai_analysis_runs")
    .select("id, project_id, story_id, chapter_range, report, credits_used, created_at")
    .eq("project_id", projectId)
    .eq("story_id", storyId)
    .order("created_at", { ascending: false });

  if (error) {
    throw mapStoryError(error);
  }

  return mapStructuralAnalysisRows(
    projectId,
    chapters,
    ((data as StructuralAnalysisRunRow[] | null) ?? []),
  );
}

export async function deleteStructuralAnalysisRun(runId: string) {
  ensureConfigured();

  const { error } = await supabase
    .from("ai_analysis_runs")
    .delete()
    .eq("id", runId);

  if (error) {
    throw mapStoryError(error);
  }
}

export async function loadStoryWorkspace(projectId: string): Promise<StoryWorkspaceView> {
  ensureConfigured();

  const [storiesResult, capacity] = await Promise.all([
    supabase
      .from("stories")
      .select("*")
      .eq("project_id", projectId),
    getProjectStoryCapacity(projectId),
  ]);

  if (storiesResult.error) {
    throw mapStoryError(storiesResult.error);
  }

  const sortedStories = sortStoryRows((storiesResult.data as StoryRow[] | null) ?? []);
  const mainStoryRow = sortedStories.find((record) => record.type === "main") ?? null;
  const sideStoryRows = sortedStories.filter((record) => record.type === "side");

  const [mainStory, sideStories] = await Promise.all([
    mainStoryRow
      ? (async (): Promise<MainStoryView> => {
          const record = hydrateStoryRecord(mainStoryRow);
          const chapters = record.mode === "chapters"
            ? await loadChapterViews(record.id)
            : [];
          return mapMainStoryRecordToView(record, chapters);
        })()
      : Promise.resolve(null),
    Promise.all(
      sideStoryRows.map(async (row) => {
        const record = hydrateStoryRecord(row);
        return resolveSideStoryView(record);
      }),
    ),
  ]);

  return {
    mainStory,
    sideStories,
    tier: capacity.tier,
  };
}

export async function getProjectStoryCapacity(
  projectId: string,
): Promise<StoryCapacitySummary> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_project_story_capacity", {
    target_project_id: projectId,
  });

  if (error) {
    throw mapStoryError(error);
  }

  const capacityRow = data?.[0];
  if (!capacityRow) {
    throw new StoryActionError("not-found", "That project is unavailable.");
  }

  return mapStoryCapacityRow({
    tier: capacityRow.tier,
    chapter_count: capacityRow.chapter_count,
    chapter_limit: capacityRow.chapter_limit,
    chapters_remaining: capacityRow.chapters_remaining,
    side_story_count: capacityRow.side_story_count,
    side_story_limit: capacityRow.side_story_limit,
    side_stories_remaining: capacityRow.side_stories_remaining,
  });
}

export async function createMainStory(
  projectId: string,
  input: CreateMainStoryInput,
) {
  ensureConfigured();
  const validated = createMainStorySchema.parse(input);

  const { data, error } = await supabase
    .from("stories")
    .insert({
      project_id: projectId,
      type: "main",
      mode: validated.mode,
      title: validated.title,
      summary: validated.summary,
      body: validated.body,
      image_paths: [],
    })
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return hydrateStoryRecord(data as StoryRow);
}

export async function updateMainStory(
  storyId: string,
  input: UpdateMainStoryInput,
) {
  ensureConfigured();
  const validated = updateMainStorySchema.parse(input);

  const { data, error } = await supabase
    .from("stories")
    .update({
      body: validated.body,
      summary: validated.summary,
    })
    .eq("id", storyId)
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return hydrateStoryRecord(data as StoryRow);
}

export async function createChapter(
  storyId: string,
  input: CreateChapterInput,
) {
  ensureConfigured();
  const validated = createChapterSchema.parse(input);

  const { count, error: countError } = await supabase
    .from("chapters")
    .select("*", { count: "exact", head: true })
    .eq("story_id", storyId);

  if (countError) {
    throw mapStoryError(countError);
  }

  const { data, error } = await supabase
    .from("chapters")
    .insert({
      story_id: storyId,
      title: validated.title,
      body: validated.body,
      status: validated.status,
      sort_order: count ?? 0,
      image_path: validated.imagePath ?? null,
    })
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return hydrateChapterRecord(data as ChapterRow);
}

export async function updateChapter(
  chapterId: string,
  input: UpdateChapterInput,
) {
  ensureConfigured();
  const validated = updateChapterSchema.parse(input);

  const { data, error } = await supabase
    .from("chapters")
    .update({
      body: validated.body,
      image_path: validated.imagePath ?? null,
      status: validated.status,
      title: validated.title,
    })
    .eq("id", chapterId)
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return hydrateChapterRecord(data as ChapterRow);
}

export async function reorderStoryChapters(
  storyId: string,
  orderedChapterIds: string[],
) {
  ensureConfigured();

  const { error } = await supabase.rpc("reorder_story_chapters", {
    target_story_id: storyId,
    ordered_chapter_ids: orderedChapterIds,
  });

  if (error) {
    throw mapStoryError(error);
  }
}

export async function assignChapterRole(
  chapterId: string,
  role: "opening" | "closing",
) {
  ensureConfigured();

  const { error } = await supabase.rpc("assign_chapter_role", {
    target_chapter_id: chapterId,
    target_role: role,
  });

  if (error) {
    throw mapStoryError(error);
  }
}

export async function saveChapterImagePath(chapterId: string, imagePath: string | null) {
  ensureConfigured();

  const { data, error } = await supabase
    .from("chapters")
    .update({
      image_path: imagePath,
    })
    .eq("id", chapterId)
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return hydrateChapterRecord(data as ChapterRow);
}

export async function removeChapterImage(chapterId: string) {
  const chapter = await getOwnedChapterById(chapterId);

  if (!chapter) {
    throw new StoryActionError("not-found", "That chapter is unavailable.");
  }

  const { data, error } = await supabase
    .from("chapters")
    .update({
      image_path: null,
    })
    .eq("id", chapterId)
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  if (chapter.image_path) {
    await deleteStoryImage(chapter.image_path).catch(() => undefined);
  }

  return hydrateChapterRecord(data as ChapterRow);
}

export async function deleteContinuationSuggestion(suggestionSetId: string) {
  ensureConfigured();

  const { error } = await supabase
    .from("continuation_suggestions")
    .delete()
    .eq("id", suggestionSetId);

  if (error) {
    throw mapStoryError(error);
  }
}

export async function updateContinuityIssueResolution(
  projectId: string,
  issueId: string,
  resolutionStatus: ContinuityIssueResolutionStatus,
) {
  ensureConfigured();

  const { data, error } = await supabase
    .from("continuity_issues")
    .update({
      resolution: resolutionStatus,
    })
    .eq("id", issueId)
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return mapContinuityIssueToView(projectId, data as ContinuityIssueRow);
}

export async function deleteContinuityIssue(issueId: string) {
  ensureConfigured();

  const { error } = await supabase
    .from("continuity_issues")
    .delete()
    .eq("id", issueId);

  if (error) {
    throw mapStoryError(error);
  }
}

export async function createSideStory(
  projectId: string,
  input: CreateSideStoryInput,
) {
  ensureConfigured();
  const validated = createSideStorySchema.parse(input);

  const { data, error } = await supabase
    .from("stories")
    .insert({
      project_id: projectId,
      type: "side",
      mode: "continuous",
      canon_status: validated.canonStatus,
      title: validated.title,
      summary: validated.summary,
      body: validated.body,
      image_paths: validated.imagePaths,
    })
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return hydrateStoryRecord(data as StoryRow);
}

export async function updateSideStory(
  storyId: string,
  input: UpdateSideStoryInput,
) {
  ensureConfigured();
  const validated = updateSideStorySchema.parse(input);

  const { data, error } = await supabase
    .from("stories")
    .update({
      canon_status: validated.canonStatus,
      title: validated.title,
      summary: validated.summary,
      body: validated.body,
      image_paths: validated.imagePaths,
    })
    .eq("id", storyId)
    .select("*")
    .single();

  if (error) {
    throw mapStoryError(error);
  }

  return hydrateStoryRecord(data as StoryRow);
}

export async function deleteSideStory(storyId: string) {
  const story = await getOwnedStoryById(storyId);

  if (!story) {
    throw new StoryActionError("not-found", "That Side Story is unavailable.");
  }

  const { error } = await supabase
    .from("stories")
    .delete()
    .eq("id", storyId);

  if (error) {
    throw mapStoryError(error);
  }

  await Promise.all(story.image_paths.map((path) => deleteStoryImage(path).catch(() => undefined)));
}

export async function appendSideStoryImage(storyId: string, imagePath: string) {
  const story = await getOwnedStoryById(storyId);

  if (!story) {
    throw new StoryActionError("not-found", "That Side Story is unavailable.");
  }

  return updateSideStory(storyId, {
    title: story.title,
    canonStatus: story.canon_status,
    summary: story.summary ?? "",
    body: story.body,
    imagePaths: [...story.image_paths, imagePath],
  });
}

export async function removeSideStoryImage(storyId: string, imagePath: string) {
  const story = await getOwnedStoryById(storyId);

  if (!story) {
    throw new StoryActionError("not-found", "That Side Story is unavailable.");
  }

  const nextImagePaths = story.image_paths.filter((path) => path !== imagePath);
  const updatedStory = await updateSideStory(storyId, {
    title: story.title,
    canonStatus: story.canon_status,
    summary: story.summary ?? "",
    body: story.body,
    imagePaths: nextImagePaths,
  });

  await deleteStoryImage(imagePath).catch(() => undefined);
  return updatedStory;
}

export async function replaceChapterImage(
  chapterId: string,
  file: File,
) {
  const chapter = await getOwnedChapterById(chapterId);

  if (!chapter) {
    throw new StoryActionError("not-found", "That chapter is unavailable.");
  }

  const story = await getOwnedStoryById(chapter.story_id);
  if (!story) {
    throw new StoryActionError("not-found", "That story is unavailable.");
  }

  const userId = await getCurrentUserId();
  const { uploadStoryImage } = await import("@/features/story/api/storyStorage");
  const nextPath = await uploadStoryImage(
    userId,
    story.project_id,
    story.id,
    "chapter",
    chapter.id,
    file,
  );

  const updated = await saveChapterImagePath(chapterId, nextPath);
  if (chapter.image_path && chapter.image_path !== nextPath) {
    await deleteStoryImage(chapter.image_path).catch(() => undefined);
  }
  return updated;
}

export async function addSideStoryImage(
  storyId: string,
  file: File,
) {
  const story = await getOwnedStoryById(storyId);

  if (!story) {
    throw new StoryActionError("not-found", "That Side Story is unavailable.");
  }

  const userId = await getCurrentUserId();
  const { uploadStoryImage } = await import("@/features/story/api/storyStorage");
  const nextPath = await uploadStoryImage(
    userId,
    story.project_id,
    story.id,
    "side_story",
    story.id,
    file,
  );

  return appendSideStoryImage(storyId, nextPath);
}

export function serializeStoryDocumentTitle(content: JSONContent) {
  const paragraph = content.content?.find((node) => node.type === "paragraph");
  const text = paragraph?.content
    ?.filter((node) => node.type === "text")
    .map((node) => node.text ?? "")
    .join("")
    .trim();

  return text && text.length > 0 ? text : "Untitled story";
}
